import { multiValue, QueryParamValue, toQueryString } from "@/utils/queryParams";
import { TMessage } from "@/i18n/tt";
import { DataTableProps, TableColumn } from "@/components-v2/DataTable";
import { transactionLabel } from "@/types/enums/TransactionType";
import { DCEvent } from "@/events/eventsDomain";
import type { OwnershipNode } from "./ownershipDomain";
import type { OwnershipFilterGroup } from "./ownershipFilterDomain";
import {
  type ComparisonOperator,
  encodeFilters,
  type FilterOption,
} from "@/components-v2/filterDialogDomain";
import { dataSourceOf, dataSourceTitle, OWNERSHIP_PERCENT_OPTIONS } from "./commonOwnershipDomain";
import Big from "big.js";
import { uniqBy } from "lodash";

export type OwnershipAccessType = "admin" | "notConnected" | "inPlatform" | "pendingInvitation";

export const ownershipAccessTypeLabels: Record<OwnershipAccessType, TMessage> = {
  admin: { key: "ownership.you-are-admin" },
  notConnected: { key: "ownership.not-connected" },
  pendingInvitation: { key: "portfolio.pendingPermissionRequest" },
  inPlatform: { key: "ownership.in-platform" },
};

export type ParentOwnership = {
  name: string;
  numberOfShares?: number;
  ownershipInPercent: number;
  votingPowerInPercent?: number;
};

type ColumnFilterConfigBase = {
  suffix?: string;
  applyFilter: (row: OwnershipNode, operator: ComparisonOperator, value: string) => boolean;
};

export type ColumnFilterConfig = ColumnFilterConfigBase &
  (
    | { type: "text" }
    | { type: "number" }
    | { type: "options"; getOptions: (rows: OwnershipNode[]) => FilterOption[] }
  );

type ColumnDef = { label: TMessage; filter?: ColumnFilterConfig };

const COLUMN_CONFIG = {
  company: {
    label: { key: "ownership.table.company" },
    filter: {
      type: "text",
      applyFilter: (row, _, value) => (row.name ?? "").toLowerCase().includes(value.toLowerCase()),
    },
  },
  organizationNumber: {
    label: { key: "ownership.table.organizationNumber" },
    filter: {
      type: "text",
      applyFilter: (row, _, value) =>
        (row.isCompany ? (row.organizationNumber ?? "") : (row.birthDate ?? ""))
          .toLowerCase()
          .includes(value.toLowerCase()),
    },
  },
  status: {
    label: { key: "common.status" },
    filter: {
      type: "options",
      getOptions: () =>
        Object.entries(ownershipAccessTypeLabels).map(([value, label]) => ({ value, label })),
      applyFilter: (row, _, value) => row.accessStatusType === value,
    },
  },
  level: {
    label: { key: "ownership.table.level" },
    filter: {
      type: "number",
      applyFilter: (row, operator, value) => applyIntFilter(row.level, operator, value),
    },
  },
  totalShares: {
    label: { key: "ownership.table.total-shares" },
    filter: {
      type: "number",
      applyFilter: (row, operator, value) => applyIntFilter(row.totalShares ?? 0, operator, value),
    },
  },
  parentName: {
    label: { key: "ownership.table.parent" },
    filter: {
      type: "text",
      applyFilter: (row, _, value) =>
        (row.parent?.name ?? "").toLowerCase().includes(value.toLowerCase()),
    },
  },
  parentShares: {
    label: { key: "ownership.table.parent.shares" },
    filter: {
      type: "number",
      applyFilter: (row, operator, value) =>
        applyIntFilter(row.parent?.numberOfShares ?? 0, operator, value),
    },
  },
  parentOwnership: {
    label: { key: "ownership.table.parent.ownership" },
    filter: {
      type: "number",
      suffix: "%",
      applyFilter: (row, operator, value) =>
        applyPercentFilter(row.parent?.ownershipInPercent ?? 0, operator, value),
    },
  },
  parentVotingPower: {
    label: { key: "ownership.table.parent.voting-power" },
    filter: {
      type: "number",
      suffix: "%",
      applyFilter: (row, operator, value) =>
        applyPercentFilter(row.parent?.votingPowerInPercent ?? 0, operator, value),
    },
  },
  integrated: {
    label: { key: "ownership.table.integrated-ownership" },
    filter: {
      type: "number",
      suffix: "%",
      applyFilter: (row, operator, value) =>
        applyPercentFilter(row.integrated ?? 0, operator, value),
    },
  },
  direct: {
    label: { key: "ownership.table.direct-ownership" },
    filter: {
      type: "number",
      suffix: "%",
      applyFilter: (row, operator, value) => applyPercentFilter(row.direct ?? 0, operator, value),
    },
  },
  votingPower: {
    label: { key: "ownership.table.voting-power" },
    filter: {
      type: "number",
      suffix: "%",
      applyFilter: (row, operator, value) =>
        applyPercentFilter(row.votingPower ?? 0, operator, value),
    },
  },
  lastTransaction: {
    label: { key: "ownership.table.lastTransaction" },
    filter: {
      type: "options",
      getOptions: (rows) => {
        const present = new Set(rows.map((row) => row.lastTransaction?.transactionType));
        return Object.entries(transactionLabel)
          .filter(([value]) => present.has(value as keyof typeof transactionLabel))
          .map(([value, label]) => ({ value, label }));
      },
      applyFilter: (row, _, value) => row.lastTransaction?.transactionType === value,
    },
  },
  lastTransactionDate: {
    label: { key: "ownership.table.lastTransactionDate" },
    filter: {
      type: "text",
      applyFilter: (row, _, value) => (row.lastTransaction?.date ?? "").includes(value),
    },
  },
  source: {
    label: { key: "ownership.table.source" },
    filter: {
      type: "options",
      getOptions: (rows) =>
        uniqBy(
          rows.flatMap((row) => {
            const value = dataSourceOf(row.ownershipSource)?.id;
            const label = dataSourceTitle(row);
            return value && label ? [{ value, label }] : [];
          }),
          (option) => option.value,
        ),
      applyFilter: (row, _, value) => dataSourceOf(row.ownershipSource)?.id === value,
    },
  },
  portfolio: {
    label: { key: "ownership.table.portfolio" },
    filter: {
      type: "text",
      applyFilter: (row, _, value) =>
        (row.portfolioName ?? "").toLowerCase().includes(value.toLowerCase()),
    },
  },
} satisfies Record<string, ColumnDef>;

export type OwnershipColumnId = keyof typeof COLUMN_CONFIG;

export const ALL_OWNERSHIP_COLUMN_IDS = Object.keys(COLUMN_CONFIG) as OwnershipColumnId[];

export const DEFAULT_OWNERSHIP_COLUMN_IDS: OwnershipColumnId[] = [
  "company",
  "organizationNumber",
  "level",
  "parentName",
  "integrated",
  "direct",
  "votingPower",
  "lastTransaction",
  "portfolio",
];

export const COLUMN_LABELS = Object.fromEntries(
  Object.entries(COLUMN_CONFIG).map(([id, def]) => [id, def.label]),
) as Record<OwnershipColumnId, TMessage>;

export const COLUMN_FILTER_CONFIGS = Object.fromEntries(
  (Object.entries(COLUMN_CONFIG) as [string, ColumnDef][]).flatMap(([id, def]) =>
    def.filter ? [[id, def.filter]] : [],
  ),
) as Partial<Record<OwnershipColumnId, ColumnFilterConfig>>;

const VALID_COLUMN_IDS = new Set<string>(ALL_OWNERSHIP_COLUMN_IDS);

export const isOwnershipColumnId = (id: string): id is OwnershipColumnId =>
  VALID_COLUMN_IDS.has(id);

export function decodeColumns(cols: QueryParamValue): OwnershipColumnId[] {
  const parsed = (multiValue(cols) ?? []).filter(isOwnershipColumnId);
  return parsed.length > 0 ? parsed : DEFAULT_OWNERSHIP_COLUMN_IDS;
}

export function buildOwnershipUrl(
  basePath: string,
  cols: OwnershipColumnId[],
  filterGroup: OwnershipFilterGroup | undefined,
): string {
  const { filterOp, filters } = encodeFilters(filterGroup);
  return basePath + toQueryString({ cols, filterOp, filters });
}

const OWNERSHIP_COLUMNS: TableColumn<OwnershipNode>[] = [
  {
    name: "company",
    label: COLUMN_LABELS.company,
    align: "left",
    getValue: (row) => row.name,
    getSortValue: (row) => row.name,
    getClassName: () => "nowrap",
    getFilterValue: (row) => row.name,
    exportToExcel: (row) => ({
      type: "string",
      value: row.name,
    }),
  },
  {
    name: "organizationNumber",
    label: COLUMN_LABELS.organizationNumber,
    align: "left",
    getValue: (row, _, { f }) =>
      row.isCompany ? f.formatOrgNo(row.organizationNumber) : f.formatDate(row.birthDate ?? ""),
    getSortValue: (row) => (row.isCompany ? row.organizationNumber : row.birthDate),
    getFilterValue: (row) => (row.isCompany ? row.organizationNumber : row.birthDate),
    exportToExcel: (row, _, { f }) =>
      !row.isCompany
        ? { type: "string", value: f.formatDate(row.birthDate) }
        : isNaN(Number(row.organizationNumber))
          ? { type: "string", value: row.organizationNumber }
          : { type: "numeric", value: Number(row.organizationNumber) },
  },
  {
    name: "status",
    label: COLUMN_LABELS.status,
    align: "left",
    getClassName: () => "nowrap",
    getSortValue: (row) => row.accessStatusType,
    getValue: (row) => ownershipAccessTypeLabels[row.accessStatusType],
  },
  {
    name: "level",
    label: COLUMN_LABELS.level,
    align: "right",
    getValue: (row) => row.level,
    getSortValue: (row) => row.level,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.level,
    }),
  },
  {
    name: "parentName",
    label: COLUMN_LABELS.parentName,
    align: "left",
    getValue: (row) => row.parent?.name,
    getSortValue: (row) => row.parent?.name,
    getClassName: () => "nowrap",
    exportToExcel: (row) => ({
      type: "string",
      value: row.parent?.name,
    }),
  },
  {
    name: "totalShares",
    label: COLUMN_LABELS.totalShares,
    align: "right",
    getValue: (row, _, { f }) => f.formatNumber(row.totalShares),
    getSortValue: (row) => row.totalShares,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.totalShares,
    }),
  },
  {
    name: "parentShares",
    label: COLUMN_LABELS.parentShares,
    align: "right",
    getValue: (row, _, { f }) => f.formatNumber(row.parent?.numberOfShares),
    getSortValue: (row) => row.parent?.numberOfShares,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.parent?.numberOfShares,
    }),
  },
  {
    name: "parentOwnership",
    label: COLUMN_LABELS.parentOwnership,
    align: "right",
    getValue: (row, _, { f }) => f.formatPercent(row.parent?.ownershipInPercent),
    getSortValue: (row) => row.parent?.ownershipInPercent,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.parent?.ownershipInPercent,
      format: "PERCENT",
    }),
  },
  {
    name: "parentVotingPower",
    label: COLUMN_LABELS.parentVotingPower,
    align: "right",
    getValue: (row, _, { f }) => f.formatPercent(row.parent?.votingPowerInPercent),
    getSortValue: (row) => row.parent?.votingPowerInPercent,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.parent?.votingPowerInPercent,
      format: "PERCENT",
    }),
  },
  {
    name: "integrated",
    label: COLUMN_LABELS.integrated,
    align: "right",
    getValue: (row, _, { f }) => f.formatPercent(row.integrated, OWNERSHIP_PERCENT_OPTIONS),
    getSortValue: (row) => row.integrated,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.integrated,
      format: "PERCENT",
    }),
  },
  {
    name: "direct",
    label: COLUMN_LABELS.direct,
    align: "right",
    getValue: (row, _, { f }) => f.formatPercent(row.direct, OWNERSHIP_PERCENT_OPTIONS),
    getSortValue: (row) => row.direct,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.direct,
      format: "PERCENT",
    }),
  },
  {
    name: "votingPower",
    label: COLUMN_LABELS.votingPower,
    align: "right",
    getValue: (row, _, { f }) => f.formatPercent(row.votingPower, OWNERSHIP_PERCENT_OPTIONS),
    getSortValue: (row) => row.votingPower,
    exportToExcel: (row) => ({
      type: "numeric",
      value: row.votingPower,
      format: "PERCENT",
    }),
  },
  {
    name: "lastTransaction",
    label: COLUMN_LABELS.lastTransaction,
    align: "left",
    getValue: (row) =>
      row.lastTransaction ? transactionLabel[row.lastTransaction.transactionType] : null,
    getSortValue: (row, _, { t }) =>
      row.lastTransaction ? t(transactionLabel[row.lastTransaction.transactionType]) : null,
    exportToExcel: (row, _, { t }) => ({
      type: "string",
      value: row.lastTransaction ? t(transactionLabel[row.lastTransaction.transactionType]) : "",
    }),
  },
  {
    name: "lastTransactionDate",
    label: COLUMN_LABELS.lastTransactionDate,
    align: "right",
    getValue: (row, _, { f }) => f.formatDate(row.lastTransaction?.date),
    getSortValue: (row) =>
      row.lastTransaction?.date ? new Date(row.lastTransaction.date).getTime() : 0,
    exportToExcel: (row, _, { f }) => ({
      type: "string",
      value: f.formatDate(row.lastTransaction?.date),
    }),
  },
  {
    name: "source",
    label: COLUMN_LABELS.source,
    align: "left",
    getClassName: () => "nowrap",
    getValue: (row) => dataSourceTitle(row) ?? null,
    getSortValue: (row, _, { t }) => {
      const label = dataSourceTitle(row);
      return label ? t(label) : null;
    },
    exportToExcel: (row, _, { t }) => {
      const label = dataSourceTitle(row);
      return { type: "string", value: label ? t(label) : "" };
    },
  },
  {
    name: "portfolio",
    label: COLUMN_LABELS.portfolio,
    align: "left",
    getValue: (row) => row.portfolioName,
    getSortValue: (row) => row.portfolioName,
    exportToExcel: (row) => ({
      type: "string",
      value: row.portfolioName,
    }),
  },
];

export const OWNERSHIP_COLUMN_MAP = new Map(OWNERSHIP_COLUMNS.map((col) => [col.name, col]));

const compareNumber = (rowVal: number, operator: ComparisonOperator, target: number): boolean => {
  if (operator === "gt") return rowVal > target;
  if (operator === "lt") return rowVal < target;
  if (operator === "eq") return Big(rowVal).eq(target);
  return true;
};

const applyIntFilter = (rowVal: number, operator: ComparisonOperator, value: string): boolean => {
  const num = parseInt(value, 10);
  return isNaN(num) || compareNumber(rowVal, operator, num);
};

const applyPercentFilter = (
  rowVal: number,
  operator: ComparisonOperator,
  value: string,
): boolean => {
  const num = parseFloat(value) / 100;
  return isNaN(num) || compareNumber(rowVal, operator, num);
};

export function prepOwnershipListTable({
  ownershipListItems,
  visibleColumns,
  search,
  onRowClick,
  selectedRow,
}: {
  ownershipListItems: OwnershipNode[];
  visibleColumns?: OwnershipColumnId[];
  search?: string;
  onRowClick?: (row: OwnershipNode) => DCEvent[];
  selectedRow?: OwnershipNode;
}): DataTableProps<OwnershipNode> {
  const tableColumns = (visibleColumns ?? DEFAULT_OWNERSHIP_COLUMN_IDS).flatMap(
    (id) => OWNERSHIP_COLUMN_MAP.get(id) ?? [],
  );
  return {
    tableColumns,
    tableData: ownershipListItems,
    getId: (row) => row.id,
    onRowClick,
    filter: search,
    rowProps: (row) => (selectedRow?.id === row.id ? { className: "bg-alt1" } : undefined),
  };
}
